import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailMachinery } from '../../dto/trail-machinery';
import { TrailMachineryService } from '../../service/trail-machinery.service';
import { Trail } from '../../dto/trail';
import { TrailMachineryDetailComponent } from '../trail-machinery-detail/trail-machinery-detail.component';

@Component({
  selector: 'dpi-trail-machinery',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule, 
    MatTooltipModule, NgIf],
  templateUrl: './trail-machinery.component.html',
  styleUrl: './trail-machinery.component.scss'
})
export class TrailMachineryComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['machinery_name', 'maintenance_check', 'outstanding_maintenance', 'last_oil_analysis_date', 'last_oil_analysis_result', 'running_hours_at_present', 'last_major_overhaul'];
  dataSource: TrailMachinery[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailMachineryService: TrailMachineryService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailMachineryService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.maintenance_check) {
              obj.maintenance_check_text = 'Yes';
            } else {
              obj.maintenance_check_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }


  selectTrailMachinery(selected: TrailMachinery) {
    if (!this.isReadonly) {
      var objCopy: TrailMachinery = { ...selected };
      const dialogRef = this._dialog.open(TrailMachineryDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailMachinery);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailMachinery.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailMachineryService.createMachinery(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailMachinery(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailMachinery) {
    let list: TrailMachinery[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailMachinery) {
    this.trailMachineryService.updateMachinery(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailMachinery) {
    let list: TrailMachinery[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        if (model.maintenance_check) {
          model.maintenance_check_text = 'Yes';
        } else {
          model.maintenance_check_text = 'No';
        }
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailMachineryService.deleteMachinery(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailMachinery[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

}

